import { X } from "lucide-react"
import { toast as sonnerToast } from "sonner"

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { classNames } from "@/utils/classNames"

type ToastVariant = "success" | "error" | "warning"

interface ToastOptions {
  id?: string | number
  /** An optional inline action (e.g. "Open") rendered next to the dismiss
   *  button — same slot the underlying `Toast` component already renders
   *  `button` through; callers just couldn't reach it before this. Clicking
   *  it also dismisses the toast, same as the underlying button prop. */
  action?: {
    label: string
    onClick: () => void
  }
}

const VARIANT_STYLES: Record<
  ToastVariant,
  {
    container: string
    dot: string
    glow: string
  }
> = {
  success: {
    container: "bg-grey1 border-success/30 text-white",
    dot: "bg-success",
    glow: "bg-success/50",
  },
  error: {
    container: "border-red1/30 bg-[rgb(76_40_38)] text-white",
    dot: "bg-red1",
    glow: "bg-red1/50",
  },
  warning: {
    container: "bg-grey1 border-primary/30 text-white",
    dot: "bg-yellow1",
    glow: "bg-yellow1/50",
  },
}

// The inline action is deliberately NOT tinted by the variant: the status
// colour already lives in the dot and border, and a solid green/red pill next
// to it reads as a second, louder signal. A quiet neutral pill matches the
// system's secondary buttons and lets the yellow primary stay the only filled
// CTA on screen.
const ACTION_BUTTON_STYLES =
  "border border-white/15 bg-white/6 text-white hover:bg-white/10 focus:ring-white/20"

function successToast(
  title: string,
  description?: string,
  options?: ToastOptions,
) {
  const { action, ...toastOptions } = options ?? {}
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        title={title}
        description={description}
        variant={"success"}
        button={action}
      />
    ),
    toastOptions,
  )
}

function errorToast(title: string, options?: ToastOptions) {
  const { action, ...toastOptions } = options ?? {}
  return sonnerToast.custom(
    (id) => <Toast id={id} title={title} variant={"error"} button={action} />,
    toastOptions,
  )
}

function warningToast(
  title: string,
  description?: string,
  options?: ToastOptions,
) {
  const { action, ...toastOptions } = options ?? {}
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        title={title}
        description={description}
        variant={"warning"}
        button={action}
      />
    ),
    toastOptions,
  )
}

function Toast(props: ToastProps) {
  const { title, description, button, id, variant = "success" } = props
  const styles = VARIANT_STYLES[variant]
  const { translate } = useTranslation()

  return (
    <div
      className={classNames(
        "flex min-h-11 w-fit min-w-[360px] items-center gap-3 rounded-full border py-3 pl-6 pr-4 shadow-[0_8px_24px_rgba(0,0,0,0.18)] md:max-w-[364px]",
        styles.container,
      )}
    >
      <span className="relative flex size-3 shrink-0 items-center justify-center">
        <span
          className={classNames(
            "toast-glow-pulse absolute size-3 rounded-full opacity-80",
            styles.glow,
          )}
        />
        <span
          className={classNames("relative size-2.5 rounded-full", styles.dot)}
        />
      </span>
      <div className="flex min-w-0 flex-1 flex-col self-center">
        <p className="text-sm font-medium">{title}</p>
        {description ? (
          <p
            className="mt-0.5 truncate text-sm text-muted-foreground"
            title={description}
          >
            {description}
          </p>
        ) : null}
      </div>
      <div className="ml-2 flex shrink-0 items-center gap-2">
        {button ? (
          <button
            type="button"
            className={classNames(
              "focus:outline-hidden rounded-full px-3 py-1 text-sm font-medium transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-offset-grey1",
              ACTION_BUTTON_STYLES,
            )}
            onClick={() => {
              button.onClick()
              sonnerToast.dismiss(id)
            }}
          >
            {button.label}
          </button>
        ) : null}
        <button
          type="button"
          aria-label={translate("TOAST/DISMISS")}
          className="focus:outline-hidden inline-flex size-6 items-center justify-center rounded-full text-grey5 transition-colors hover:text-white focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-grey1"
          onClick={() => {
            sonnerToast.dismiss(id)
          }}
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  )
}

interface ToastProps {
  id: string | number
  title: string
  description?: string
  variant?: ToastVariant
  button?: {
    label: string
    onClick: () => void
  }
}

export const toast = {
  success: successToast,
  error: errorToast,
  warning: warningToast,
}
